/**
 * Phase 0.5 Component F — shared types for the viewer-projection
 * primitives. Mirrors the Go-side `viewmode.PolicyResult` and resolver
 * shapes; kept hand-typed so the UI doesn't need to import generated
 * proto messages everywhere.
 */ /** The Phase 3c legal-lane sensitivity values, enforced as a hard boundary. */ export const LEGAL_RESTRICTED_SENSITIVITIES = [
    'legal_privileged',
    'source_selection_sensitive',
    'protective_order'
];
/** Reports whether a sensitivity value is one of the legal-lane hard-boundary values. */ export function isLegalRestricted(sensitivity) {
    return !!sensitivity && LEGAL_RESTRICTED_SENSITIVITIES.includes(sensitivity);
}
/**
 * programRef builds the ObjectRef for a delivery program — the projection most
 * program-child surfaces (EAC, CLINs, PoPs, optional tasks, CPARS/QASP/DSO)
 * resolve their masking against. One helper so every surface keys masking off
 * the same object_type/object_id pair instead of hand-rolling the literal.
 */ export function programRef(programId) {
    return {
        kind: 'program',
        id: programId,
        objectType: 'delivery.programs',
        objectId: programId
    };
}
/**
 * isFieldMasked matches a field name against the resolver's mask list,
 * tolerating both Pascal/CamelCase props ("OwnerEmail") and the snake_case
 * the resolver/proto emit ("owner_email"). Single source of truth shared by
 * <MaskedField> and useView so the two can never drift.
 */ export function isFieldMasked(name, maskedFields) {
    if (maskedFields.length === 0) return false;
    // "*" is the resolver's mask-all sentinel: a redacting view mode whose kind
    // has no sensitive-field registry entry fails CLOSED (everything masked)
    // instead of silently rendering every value.
    if (maskedFields.includes('*')) return true;
    const snake = name.replace(/([A-Z])/g, '_$1').replace(/^_/, '').toLowerCase();
    return maskedFields.includes(name) || maskedFields.includes(snake);
}
/**
 * fieldIsMasked is the full redaction predicate for one field under a
 * projection. The resolver's maskedFields list is AUTHORITATIVE for both
 * Masked and Simplified views: it is the registry's per-kind sensitive set
 * minus the holder's D1(b) unmask carve-outs. The old blanket rule
 * ("masked redacts everything") predated carve-outs and silently overrode
 * them on every <MaskedField> surface — a quality viewer's own CPARS score
 * rendered "Restricted" while the server-projected surfaces showed it.
 * The FE must render exactly what the resolver decided, nothing stricter.
 */ export function fieldIsMasked(viewMode, maskedFields, name) {
    // no_view never reaches here (the surface renders NoViewSlot), and the
    // real resolver only populates the list on Masked/Simplified — but honor
    // a non-empty list on ANY mode as defense in depth.
    void viewMode;
    return isFieldMasked(name, maskedFields);
}
/**
 * financialSectionVisible is the shared predicate for whether a viewer can
 * reach the Financial section at all — KpiSpine's tile and ProgramSidebar's
 * nav item both call this instead of re-deriving it, so they can never
 * disagree. Visible when the viewer can see at least one of the two figures
 * the Financial Health tab exposes (funding consumption, burn rate) — e.g. an
 * exec_viewer-style holder of only `contract_topline` sees funding progress
 * and reaches the section even though a fully-masked viewer cannot.
 */ export function financialSectionVisible(fieldMasked) {
    return !fieldMasked('funding_consumed_pct') || !fieldMasked('burn_rate_cents');
}
export const FAIL_CLOSED = {
    decision: 'deny',
    viewMode: 'no_view',
    maskedFields: [],
    actionBlocked: [],
    reason: 'fail-closed',
    matchedPolicyId: '',
    auditRequired: false
};
